import classnames from "classnames";
import Papa from "papaparse";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Col, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";

import { useProfile } from "Components/Hooks/UserHooks";
import {
  CsvFieldMapping,
  CsvImportField,
  CsvImportRequest,
  MAX_IMPORT_RECORDS,
  autoMapCsvFields,
} from "./csvImportTypes";

const IMPORT_TIMEOUT_MS = 5 * 60 * 1000;
const PREVIEW_LIMIT = 10;

export type CsvImportWizardProps = {
  fields: CsvImportField[];
  mapRow: (row: Record<string, string>, mappings: CsvFieldMapping) => any;
  importRecords: CsvImportRequest;
  templatePath: string;
  templateFileName: string;
  failedFileName: string;
  recordLabel: string;
  maxRecords?: number;
  batchSize?: number;
  onImported?: () => void;
};

async function mergeCsvBlobs(blobs: Blob[]): Promise<Blob | null> {
  if (!blobs.length) return null;
  if (blobs.length === 1) return blobs[0];

  const texts = await Promise.all(blobs.map((blob) => blob.text()));
  let header = "";
  const bodies: string[] = [];

  texts.forEach((rawText) => {
    const text = rawText.replace(/^\uFEFF/, "");
    const firstNewLine = text.indexOf("\n");
    if (firstNewLine < 0) return;
    if (!header) header = text.slice(0, firstNewLine).replace(/\r$/, "");
    const body = text.slice(firstNewLine + 1).replace(/\s+$/, "");
    if (body) bodies.push(body);
  });

  if (!header) return null;
  return new Blob(["\uFEFF", header, "\n", bodies.join("\n"), "\n"], {
    type: "text/csv;charset=utf-8",
  });
}

const PaneHeader: React.FC<{
  icon: string;
  title: string;
  description: React.ReactNode;
  step: number;
}> = ({ icon, title, description, step }) => (
  <div className="d-flex align-items-start gap-3 mb-4 pb-3 border-bottom">
    <div
      className="rounded-2 d-flex align-items-center justify-content-center bg-primary-subtle flex-shrink-0"
      style={{ width: 38, height: 38 }}
    >
      <i className={`${icon} text-primary`} style={{ fontSize: 18 }} />
    </div>
    <div className="flex-grow-1">
      <h5 className="mb-0 fw-semibold">{title}</h5>
      <p className="text-muted mb-0 mt-1">{description}</p>
    </div>
    <span className="badge bg-secondary-subtle text-secondary rounded-pill px-3 py-2 fs-12">
      Step {step} of 4
    </span>
  </div>
);

const Metric: React.FC<{
  icon: string;
  label: string;
  value: React.ReactNode;
  color?: string;
}> = ({ icon, label, value, color = "text-secondary" }) => (
  <div className="d-flex align-items-center gap-3 px-4 py-3 h-100">
    <i className={`${icon} ${color}`} style={{ fontSize: 22 }} />
    <div>
      <div className="text-muted mb-1" style={{ fontSize: 12 }}>{label}</div>
      <div className="fw-semibold" style={{ fontSize: 20, color: "#1e293b" }}>{value}</div>
    </div>
  </div>
);

const CsvImportWizard: React.FC<CsvImportWizardProps> = ({
  fields,
  mapRow,
  importRecords,
  templatePath,
  templateFileName,
  failedFileName,
  recordLabel,
  maxRecords = MAX_IMPORT_RECORDS,
  batchSize,
  onImported,
}) => {
  const { token } = useProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const downloadUrlRef = useRef<string | null>(null);

  const [activeTab, setActiveTab] = useState(1);
  const [visitedTabs, setVisitedTabs] = useState<number[]>([1]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [mappings, setMappings] = useState<CsvFieldMapping>({});
  const [status, setStatus] = useState<"idle" | "running" | "done">("idle");
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<{
    total: number;
    successful: number;
    failed: number;
    downloadUrl: string | null;
  } | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const fileReady = !!selectedFile && rows.length > 0 && !parseError;
  const requiredMapped = fields
    .filter((field) => field.required)
    .every((field) => !!mappings[field.key]);
  const mappedFields = fields.filter((field) => !!mappings[field.key]);
  const requiredFields = fields.filter((field) => field.required);
  const mappedRequired = requiredFields.filter((field) => !!mappings[field.key]);
  const effectiveRows = Math.min(rows.length, maxRecords);
  const progressPercent = progress.total
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  const stopProgress = useCallback(() => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = null;
  }, []);

  const revokeDownload = useCallback(() => {
    if (downloadUrlRef.current) URL.revokeObjectURL(downloadUrlRef.current);
    downloadUrlRef.current = null;
  }, []);

  useEffect(() => () => {
    stopProgress();
    revokeDownload();
  }, [revokeDownload, stopProgress]);

  const reset = () => {
    stopProgress();
    revokeDownload();
    setActiveTab(1);
    setVisitedTabs([1]);
    setSelectedFile(null);
    setDragging(false);
    setHeaders([]);
    setRows([]);
    setParseError(null);
    setMappings({});
    setStatus("idle");
    setProgress({ current: 0, total: 0 });
    setResults(null);
    setErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const changeTab = (tab: number) => {
    if (tab < 1 || tab > 4 || tab === activeTab) return;
    if (tab > 1 && !fileReady) return;
    if (tab > 2 && !requiredMapped) return;
    if (tab > activeTab + 1 && !visitedTabs.includes(tab)) return;
    setActiveTab(tab);
    setVisitedTabs((current) => current.includes(tab) ? current : [...current, tab]);
  };

  const pickFile = (files: FileList | File[]) => {
    const file = Array.isArray(files) ? files[0] : files[0];
    if (!file) return;
    setSelectedFile(file);
    setHeaders([]);
    setRows([]);
    setMappings({});
    setParseError(null);
    setStatus("idle");
    setResults(null);
    setErrors([]);
    revokeDownload();

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setParseError("Please upload a CSV file.");
      return;
    }

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (header) => header.trim(),
      complete: (parsed) => {
        const parsedHeaders = (parsed.meta.fields ?? []).filter(Boolean);
        if (!parsedHeaders.length) {
          setParseError("No column headers were found in this CSV file.");
          return;
        }
        if (!parsed.data.length) {
          setParseError("The CSV file does not contain any data rows.");
          return;
        }
        if (parsed.errors.some((error) => error.type === "Quotes")) {
          setParseError("The CSV contains an invalid quoted value.");
          return;
        }
        setHeaders(parsedHeaders);
        setRows(parsed.data);
        setMappings(autoMapCsvFields(fields, parsedHeaders));
      },
      error: (error) => setParseError(error.message),
    });
  };

  const runImport = async () => {
    const selectedRows = rows.slice(0, maxRecords);
    const total = selectedRows.length;
    setStatus("running");
    setErrors([]);
    setProgress({ current: 0, total });

    if (!batchSize) {
      let simulatedPercent = 0;
      progressTimerRef.current = setInterval(() => {
        simulatedPercent += (90 - simulatedPercent) * 0.07;
        setProgress({
          current: Math.round((total * simulatedPercent) / 100),
          total,
        });
      }, 400);
    }

    const data = selectedRows.map((row) => mapRow(row, mappings));
    const size = Math.max(1, Math.min(batchSize ?? total, total));
    const batches: any[][] = [];
    for (let index = 0; index < data.length; index += size) {
      batches.push(data.slice(index, index + size));
    }

    let processed = 0;
    let successful = 0;
    let failed = 0;
    const failedCsvBlobs: Blob[] = [];

    const processBatch = async (batch: any[]): Promise<void> => {
      try {
        const response = await importRecords(
          { data: batch },
          token,
          { timeoutMs: IMPORT_TIMEOUT_MS }
        );
        const batchSuccessful =
          response.metadata?.data?.successful ?? batch.length;
        const batchFailed =
          response.metadata?.data?.failed ?? batch.length - batchSuccessful;
        successful += batchSuccessful;
        failed += batchFailed;
        if (batchFailed > 0 && response.blob?.size) {
          failedCsvBlobs.push(response.blob);
        }
        processed += batch.length;
        setProgress({ current: processed, total });
      } catch (error: any) {
        if (error?.response?.status === 413 && batch.length > 1) {
          const middle = Math.ceil(batch.length / 2);
          await processBatch(batch.slice(0, middle));
          await processBatch(batch.slice(middle));
          return;
        }
        throw error;
      }
    };

    try {
      for (const batch of batches) await processBatch(batch);
      const failedCsv = await mergeCsvBlobs(failedCsvBlobs);
      const downloadUrl = failedCsv ? URL.createObjectURL(failedCsv) : null;
      downloadUrlRef.current = downloadUrl;
      setResults({ total, successful, failed, downloadUrl });
      if (successful > 0) onImported?.();
    } catch (error: any) {
      const timeout = error?.code === "ECONNABORTED" || error?.name === "AbortError";
      const remaining = total - processed;
      const message = timeout
        ? `The import stopped after ${processed.toLocaleString()} records because a batch timed out.`
        : error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Import request failed.";
      const failedCsv = await mergeCsvBlobs(failedCsvBlobs);
      const downloadUrl = failedCsv ? URL.createObjectURL(failedCsv) : null;
      downloadUrlRef.current = downloadUrl;
      setErrors([
        message,
        remaining > 0
          ? `${remaining.toLocaleString()} records were not processed and can be imported again.`
          : "",
      ].filter(Boolean));
      setResults({
        total,
        successful,
        failed: failed + remaining,
        downloadUrl,
      });
      if (successful > 0) onImported?.();
    } finally {
      stopProgress();
      setProgress({ current: processed || total, total });
      setStatus("done");
    }
  };

  const steps = [
    [1, "File Upload", "ri-upload-cloud-2-line"],
    [2, "Field Mapping", "ri-node-tree"],
    [3, "Data Preview", "ri-eye-line"],
    [4, "Import", "ri-checkbox-circle-line"],
  ] as const;

  return (
    <>
      <div className="step-arrow-nav mb-4">
        <Nav className="nav-pills nav-justified custom-nav">
          {steps.map(([tab, label, icon]) => {
            const locked =
              (tab > 1 && !fileReady) ||
              (tab > 2 && !requiredMapped) ||
              (tab > activeTab + 1 && !visitedTabs.includes(tab));
            return (
              <NavItem key={tab}>
                <NavLink
                  href="#"
                  className={classnames(
                    "p-3 fs-15 d-flex align-items-center justify-content-center gap-2",
                    { active: activeTab === tab, done: activeTab > tab, "text-muted": locked }
                  )}
                  style={locked ? { pointerEvents: "none", opacity: 0.45 } : undefined}
                  onClick={(event) => { event.preventDefault(); changeTab(tab); }}
                >
                  <i className={activeTab > tab ? "ri-check-line" : locked ? "ri-lock-line" : icon} />
                  <span className="d-none d-sm-inline">{label}</span>
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>
      </div>

      <TabContent activeTab={activeTab} className="pt-3">
        <TabPane tabId={1}>
          <PaneHeader
            icon="ri-upload-cloud-2-line"
            title="File Upload"
            description={`Upload a CSV file containing your ${recordLabel} records. The first row must contain column names.`}
            step={1}
          />
          <div
            role="button"
            tabIndex={0}
            className="d-flex flex-column align-items-center justify-content-center rounded-3"
            style={{
              minHeight: 250,
              padding: 32,
              cursor: "pointer",
              background: dragging ? "#f0f4ff" : fileReady ? "#f8fff8" : "#f8f9fc",
              border: `2px dashed ${dragging ? "#4361ee" : parseError ? "#ef4444" : fileReady ? "#22c55e" : "#d1d9e6"}`,
            }}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") fileInputRef.current?.click();
            }}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => { event.preventDefault(); setDragging(false); }}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              pickFile(event.dataTransfer.files);
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="d-none"
              onChange={(event) => {
                if (event.target.files?.length) pickFile(event.target.files);
                event.target.value = "";
              }}
            />
            <i
              className={parseError ? "ri-error-warning-line text-danger" : fileReady ? "ri-file-check-line text-success" : "ri-upload-cloud-2-line text-secondary"}
              style={{ fontSize: 42 }}
            />
            <div className="fw-semibold mt-3">
              {selectedFile ? selectedFile.name : dragging ? "Drop your file here" : "Drag & drop your CSV file here"}
            </div>
            <div className="text-muted mt-1">
              {selectedFile
                ? `${(selectedFile.size / 1024).toFixed(2)} KB${fileReady ? ` · ${rows.length.toLocaleString()} rows` : ""}`
                : "or click to browse"}
            </div>
          </div>
          {parseError && <Alert color="danger" className="mt-3 mb-0">{parseError}</Alert>}
          <div className="d-flex align-items-center gap-2 mt-4 pt-3 border-top">
            <a className="btn btn-soft-primary btn-sm" href={templatePath} download={templateFileName}>
              <i className="ri-download-2-line me-1" />Download Template
            </a>
            <button className="btn btn-ghost-secondary btn-sm" type="button" onClick={reset}>
              <i className="ri-restart-line me-1" />Start Over
            </button>
            <button className="btn btn-primary ms-auto" type="button" disabled={!fileReady} onClick={() => changeTab(2)}>
              Proceed to Field Mapping <i className="ri-arrow-right-line ms-1" />
            </button>
          </div>
        </TabPane>

        <TabPane tabId={2}>
          <PaneHeader
            icon="ri-node-tree"
            title="Field Mapping"
            description="Match each CSV column to the corresponding destination field."
            step={2}
          />
          <div className="rounded-3 border overflow-hidden">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr><th className="ps-3 py-3">Destination Field</th><th>CSV Column</th><th className="text-center">Status</th></tr>
              </thead>
              <tbody>
                {fields.map((field) => {
                  const mapped = !!mappings[field.key];
                  return (
                    <tr key={field.key}>
                      <td className="ps-3 fw-medium">
                        {field.label}{field.required && <span className="text-danger ms-1">*</span>}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          style={{ maxWidth: 340 }}
                          value={mappings[field.key] ?? ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            setMappings((current) => ({ ...current, [field.key]: value || undefined }));
                          }}
                        >
                          <option value="">— Not mapped —</option>
                          {headers.map((header) => <option key={header} value={header}>{header}</option>)}
                        </select>
                      </td>
                      <td className="text-center">
                        <span className={`badge rounded-pill ${mapped ? "bg-success-subtle text-success" : "bg-light text-muted"}`}>
                          {mapped ? "Mapped" : "Skipped"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!requiredMapped && <div className="text-danger mt-2"><i className="ri-error-warning-line me-1" />Map all required fields to continue.</div>}
          <div className="d-flex mt-4 pt-3 border-top">
            <button className="btn btn-light" type="button" onClick={() => changeTab(1)}>Back</button>
            <button className="btn btn-primary ms-auto" type="button" disabled={!requiredMapped} onClick={() => changeTab(3)}>
              Preview Data <i className="ri-arrow-right-line ms-1" />
            </button>
          </div>
        </TabPane>

        <TabPane tabId={3}>
          <PaneHeader
            icon="ri-eye-line"
            title="Data Preview"
            description={<>Showing the first {Math.min(PREVIEW_LIMIT, rows.length)} of <strong>{rows.length.toLocaleString()}</strong> rows.</>}
            step={3}
          />
          <div className="rounded-3 border" style={{ maxHeight: 380, overflow: "auto" }}>
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th className="ps-3">#</th>
                  {mappedFields.map((field) => <th key={field.key}>{field.label}<div className="fw-normal text-muted fs-12">← {mappings[field.key]}</div></th>)}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, PREVIEW_LIMIT).map((row, index) => (
                  <tr key={index}>
                    <td className="ps-3 text-muted">{index + 1}</td>
                    {mappedFields.map((field) => {
                      const column = mappings[field.key];
                      return <td key={field.key}>{column ? row[column] || "—" : "—"}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex mt-4 pt-3 border-top">
            <button className="btn btn-light" type="button" onClick={() => changeTab(2)}>Back</button>
            <button className="btn btn-primary ms-auto" type="button" onClick={() => changeTab(4)}>
              Proceed to Import <i className="ri-arrow-right-line ms-1" />
            </button>
          </div>
        </TabPane>

        <TabPane tabId={4}>
          {status === "idle" && (
            <>
              <PaneHeader icon="ri-upload-2-line" title="Ready to Import" description="Confirm the summary, then start the import." step={4} />
              {rows.length > maxRecords && (
                <Alert color="warning">Only the first {maxRecords.toLocaleString()} rows will be imported. Split the file to import the remainder.</Alert>
              )}
              {!!batchSize && effectiveRows > batchSize && (
                <Alert color="info">
                  The records will be processed in {Math.ceil(effectiveRows / batchSize).toLocaleString()} batches of up to {batchSize.toLocaleString()} to keep each upload reliable.
                </Alert>
              )}
              <div className="border rounded-3 overflow-hidden mb-4">
                <Row className="g-0">
                  <Col md={4} className="border-end"><Metric icon="ri-stack-line" label="Rows to import" value={effectiveRows.toLocaleString()} /></Col>
                  <Col md={4} className="border-end"><Metric icon="ri-node-tree" label="Fields mapped" value={<>{mappedFields.length}<span className="text-muted fs-13 fw-normal"> / {fields.length}</span></>} /></Col>
                  <Col md={4}><Metric icon="ri-list-check-3" label="Required fields" value={<>{mappedRequired.length}<span className="text-muted fs-13 fw-normal"> / {requiredFields.length}</span></>} /></Col>
                </Row>
              </div>
              <div className="d-flex pt-3 border-top">
                <button className="btn btn-light" type="button" onClick={reset}>Start Over</button>
                <button className="btn btn-primary ms-auto" type="button" onClick={runImport}>Start Import <i className="ri-upload-2-line ms-1" /></button>
              </div>
            </>
          )}

          {status === "running" && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" style={{ width: 58, height: 58 }} />
              <h5>Importing {recordLabel}…</h5>
              <p className="text-muted">Keep this window open while the records are processed.</p>
              <div className="mx-auto" style={{ maxWidth: 460 }}>
                <div className="d-flex justify-content-between mb-2"><span>Progress</span><strong>{progressPercent}%</strong></div>
                <div className="progress" style={{ height: 8 }}><div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${progressPercent}%` }} /></div>
              </div>
            </div>
          )}

          {status === "done" && results && (
            <div className="py-3 mx-auto" style={{ maxWidth: 820 }}>
              <div className="d-flex align-items-center gap-3 mb-4">
                <div className={`d-flex align-items-center justify-content-center rounded-circle ${results.failed ? "bg-warning-subtle text-warning" : "bg-success-subtle text-success"}`} style={{ width: 52, height: 52 }}>
                  <i className={results.failed ? "ri-alert-line" : "ri-check-line"} style={{ fontSize: 25 }} />
                </div>
                <div className="flex-grow-1">
                  <h5 className="mb-1">{results.failed ? "Import completed with errors" : "Import successful"}</h5>
                  <p className="text-muted mb-0">{results.successful.toLocaleString()} of {results.total.toLocaleString()} records imported successfully.</p>
                </div>
                <span className={`badge rounded-pill px-3 py-2 ${results.failed ? "bg-warning-subtle text-warning" : "bg-success-subtle text-success"}`}>
                  {results.failed ? "Needs review" : "Completed"}
                </span>
              </div>
              <div className="border rounded-3 overflow-hidden mb-4">
                <Row className="g-0">
                  <Col md={4} className="border-end"><Metric icon="ri-stack-line" label="Processed" value={results.total.toLocaleString()} /></Col>
                  <Col md={4} className="border-end"><Metric icon="ri-user-follow-line" label="Imported" value={results.successful.toLocaleString()} color="text-success" /></Col>
                  <Col md={4}><Metric icon="ri-file-warning-line" label="Failed" value={results.failed.toLocaleString()} color="text-danger" /></Col>
                </Row>
              </div>
              {results.failed > 0 && results.downloadUrl && (
                <div className="d-flex align-items-center gap-3 border rounded-3 p-3 mb-4">
                  <i className="ri-file-download-line text-secondary fs-4" />
                  <div className="flex-grow-1"><div className="fw-medium">Review failed records</div><div className="text-muted fs-13 mt-1">Download the CSV, correct each failed row, and import it again.</div></div>
                  <a className="btn btn-outline-danger" href={results.downloadUrl} download={failedFileName}><i className="ri-download-2-line me-1" />Download CSV</a>
                </div>
              )}
              {errors.length > 0 && <Alert color="danger">{errors.map((error, index) => <div key={index}>{error}</div>)}</Alert>}
              <div className="pt-3 border-top"><button className="btn btn-light" type="button" onClick={reset}><i className="ri-restart-line me-1" />Import Another File</button></div>
            </div>
          )}
        </TabPane>
      </TabContent>
    </>
  );
};

export default CsvImportWizard;
